import type { ShadowComparisonResult } from "@expert/gaia";

import { RootAnalyticsProvider } from "@asurion-core/eventualize-react";
import { GlobalLoader } from "@expert/common-ui";
import { GaiaWebsocketProvider } from "@expert/gaia";
import { useCallback, useEffect } from "react";

import type { ReusableMainContainerProps } from "../components";

import { sendBusinessEvent } from "../analytics";
import { Header, PageContainer, ReusableMainContainer, SidebarFeedbackButton, StandaloneTimeline } from "../components";
import { useInitializers } from "../initializers";
import { useExpertAssistStore, useLoggerWithContextStore } from "../state";
import { env } from "../utils/env";
import classes from "./Home.module.css";

function MainContainer(props: Omit<ReusableMainContainerProps, "className">) {
    return <ReusableMainContainer {...props} className={classes.mainContainer} />;
}

export function Home() {
    useInitializers();
    const { loggerWithContext } = useLoggerWithContextStore();
    const { sessionId, token, expertProfile } = useExpertAssistStore();

    useEffect(() => {
        loggerWithContext.info({ module: "Home", domain: window.location.hostname }, "Home page loaded");
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    // Dark launch: in nonprod (where a Simplr URL is configured) open the Simplr websocket in
    // shadow mode alongside GAIA and emit an analytic for any message difference.
    const handleShadowComparison = useCallback((result: ShadowComparisonResult) => {
        sendBusinessEvent({ eventName: "SimplrWebsocketShadowComparison", attributes: { ...result } });
    }, []);

    if (!sessionId) return <GlobalLoader msg="Loading Expert Assist..." />;

    return (
        <RootAnalyticsProvider>
            <GaiaWebsocketProvider
                logger={loggerWithContext}
                identity={expertProfile.id}
                application="expert-workspace" // TODO: update this to expert-assist once supported by gaia
                token={token ?? ""}
                url={env.gaiaWebsocketUrl}
                shadowUrl={env.simplrWebsocketUrl}
                // expert-assist runs the shadow in NONPROD ONLY (no feature flag is wired here — the
                // Staples pilot is scoped to expert-ui). `!env.isProd` keeps it off in prod builds even
                // though prod env carries a Simplr URL; do not add a prod path here without a scoped flag.
                shadowEnabled={Boolean(env.simplrWebsocketUrl) && !env.isProd}
                onShadowComparison={handleShadowComparison}
            >
                <PageContainer>
                    <Header />
                    <MainContainer direction="column" justify="flex-end" pb="sm" pt="3xl">
                        <StandaloneTimeline />
                    </MainContainer>
                    <SidebarFeedbackButton />
                </PageContainer>
            </GaiaWebsocketProvider>
        </RootAnalyticsProvider>
    );
}
